import { AssistantContentPart } from '@paragon/contract-library';

import { measurePdfDrawing } from './drawingGeometry';

/**
 * A file the designer attached to a message, read in the browser and sent with it.
 */
export type Attachment =
  | {
      readonly kind: 'image';
      readonly name: string;
      readonly dataUrl: string;
      /** Tells the model what the image is, when the panel made it rather than the designer picking a file. */
      readonly description?: string;
    }
  | {
      readonly kind: 'pdf';
      readonly name: string;
      readonly dataUrl: string;
      /** The truss measured from the PDF's vector lines, when it's a drawing with a stated scale. */
      readonly measurements?: string;
    }
  | { readonly kind: 'text'; readonly name: string; readonly text: string };

export const MAX_ATTACHMENTS_PER_MESSAGE = 5;

export const SKETCH_DESCRIPTION =
  'This is a screenshot of the app that the designer drew on to show what they mean. Their marks are the freehand ' +
  'lines on top; read them with their message.';

const MEGABYTE = 1024 * 1024;

/**
 * The largest file the panel accepts, by kind. PDFs stay under the model's 32 MB limit. Images can be larger, since
 * they're shrunk to fit the model's 5 MB limit before they're sent. Text files go into the message itself, so they're
 * kept small.
 */
export const MAX_ATTACHMENT_BYTES: Record<Attachment['kind'], number> = {
  image: 30 * MEGABYTE,
  pdf: 30 * MEGABYTE,
  text: 1 * MEGABYTE,
};

/**
 * The most a message's attachments may add up to. Every model call in a conversation resends them, so this keeps a
 * conversation with a few attachments under DesignServer's request limit.
 */
export const MAX_ATTACHMENT_BYTES_PER_MESSAGE = 32 * MEGABYTE;

/**
 * The model reads images up to 5 MB, and downsamples anything with a longer edge than this, so larger images only cost
 * time to send.
 */
export const MAX_IMAGE_BYTES = 3.5 * MEGABYTE;
export const MAX_IMAGE_EDGE = 2576;

export const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
const TEXT_EXTENSIONS = ['.txt', '.csv', '.json', '.md'];

/** What the file input offers to pick. */
export const ATTACHMENT_ACCEPT = [...IMAGE_TYPES, 'application/pdf', ...TEXT_EXTENSIONS].join(',');

export class AttachmentError extends Error {}

function hasExtension(name: string, extensions: readonly string[]) {
  const lowerCaseName = name.toLowerCase();
  return extensions.some((extension) => lowerCaseName.endsWith(extension));
}

export function getAttachmentKind(file: Pick<File, 'name' | 'type'>): Attachment['kind'] | null {
  if (IMAGE_TYPES.includes(file.type)) return 'image';
  if (file.type === 'application/pdf' || hasExtension(file.name, ['.pdf'])) return 'pdf';
  if (file.type.startsWith('text/') || hasExtension(file.name, TEXT_EXTENSIONS)) return 'text';
  return null;
}

function readAsDataUrl(file: Blob) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/**
 * Returns the size to redraw an image at so the model can read it, or `null` if it can be sent as it is.
 */
export function getImageResize(width: number, height: number, bytes: number): { width: number; height: number } | null {
  const longEdge = Math.max(width, height);
  if (bytes <= MAX_IMAGE_BYTES && longEdge <= MAX_IMAGE_EDGE) return null;

  const scale = Math.min(1, MAX_IMAGE_EDGE / longEdge);
  return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) };
}

/**
 * Reads an image as a data URL the model accepts, redrawing it smaller first when it's too large. Drawings keep their
 * lines best as PNG, so a redrawn image is only re-encoded as JPEG if the PNG is still over the limit.
 */
async function readImageForModel(file: File): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const resize = getImageResize(bitmap.width, bitmap.height, file.size);
  if (resize == null) {
    bitmap.close();
    return readAsDataUrl(file);
  }

  const canvas = new OffscreenCanvas(resize.width, resize.height);
  const context = canvas.getContext('2d');
  if (context == null) throw new AttachmentError(`Couldn’t shrink ${file.name} to send it.`);
  // A white background keeps transparent drawings legible once they're flattened to JPEG.
  context.fillStyle = 'white';
  context.fillRect(0, 0, resize.width, resize.height);
  context.drawImage(bitmap, 0, 0, resize.width, resize.height);
  bitmap.close();

  let blob = await canvas.convertToBlob({ type: 'image/png' });
  for (const quality of [0.9, 0.8, 0.65]) {
    if (blob.size <= MAX_IMAGE_BYTES) break;
    blob = await canvas.convertToBlob({ type: 'image/jpeg', quality });
  }
  if (blob.size > MAX_IMAGE_BYTES) throw new AttachmentError(`${file.name} is too detailed to send, even shrunk.`);
  return readAsDataUrl(blob);
}

/**
 * Reads a file the designer attached, or throws an {@link AttachmentError} saying why it can't be attached.
 */
export async function readAttachment(file: File): Promise<Attachment> {
  const kind = getAttachmentKind(file);
  if (kind == null) throw new AttachmentError(`${file.name} isn’t an image, PDF, or text file.`);
  if (file.size > MAX_ATTACHMENT_BYTES[kind])
    throw new AttachmentError(`${file.name} is over ${MAX_ATTACHMENT_BYTES[kind] / MEGABYTE} MB.`);

  switch (kind) {
    case 'image':
      return { kind, name: file.name, dataUrl: await readImageForModel(file) };
    case 'pdf': {
      const measurements = await measurePdfDrawing(file.name, new Uint8Array(await file.arrayBuffer()));
      // Some browsers leave the type off; the model needs it in the data URL.
      return {
        kind,
        name: file.name,
        dataUrl: (await readAsDataUrl(file)).replace(/^data:[^;]*;/, 'data:application/pdf;'),
        ...(measurements != null ? { measurements } : {}),
      };
    }
    case 'text':
      return { kind, name: file.name, text: await file.text() };
  }
}

/**
 * About how many bytes an attachment adds to a request.
 */
export function attachmentSize(attachment: Attachment) {
  if (attachment.kind === 'text') return attachment.text.length;
  return attachment.dataUrl.length + (attachment.kind === 'pdf' ? (attachment.measurements?.length ?? 0) : 0);
}

/**
 * The content of the designer's message: their text, then each attachment as the model reads it.
 */
export function buildUserContent(text: string, attachments: readonly Attachment[]): string | AssistantContentPart[] {
  if (attachments.length === 0) return text;

  const parts: AssistantContentPart[] = [{ type: 'text', text }];
  for (const attachment of attachments) {
    switch (attachment.kind) {
      case 'image':
        parts.push({
          type: 'text',
          text: `Attached image: ${attachment.name}${attachment.description ? `. ${attachment.description}` : ''}`,
        });
        parts.push({ type: 'image_url', image_url: { url: attachment.dataUrl } });
        break;
      case 'pdf':
        parts.push({ type: 'file', file: { filename: attachment.name, file_data: attachment.dataUrl } });
        if (attachment.measurements != null) parts.push({ type: 'text', text: attachment.measurements });
        break;
      case 'text':
        parts.push({ type: 'text', text: `Attached file ${attachment.name}:\n\n${attachment.text}` });
        break;
    }
  }
  return parts;
}
